import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Zap, Globe, Key, MessageCircle, AlertCircle, Gift, Users } from 'lucide-react';
import '../styles/MainScreen.css';
import Logo from '../assets/logo.svg';
import { checkUserAuthorization, getPartnerIds } from '../api';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const showRaffleButton = false;

function MainScreen({ setSelectedVPN, vpnTypes }) {
  const navigate = useNavigate();
  const [telegramId, setTelegramId] = useState(null);
  const [isAuthorized, setIsAuthorized] = useState(null);
  const [hasPartnerId, setHasPartnerId] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let tgId = null;

    if (window.Telegram?.WebApp) {
      const user = window.Telegram.WebApp.initDataUnsafe?.user;
      if (user?.id) {
        tgId = user.id.toString();
        setTelegramId(tgId);
      }
    }

    // Для локальной разработки
    if (process.env.NODE_ENV === 'development' && !tgId) {
      const params = new URLSearchParams(window.location.search);
      tgId = params.get('tg_id') || '328036431';
      setTelegramId(tgId);
    }

    if (tgId) {
      const checkStatus = async () => {
        setLoading(true);

        // 1. Проверка авторизации
        const authResult = await checkUserAuthorization(tgId);
        setIsAuthorized(authResult.present);

        // 2. Проверка партнёрского ID (только если авторизован)
        if (authResult.present) {
          try {
            const partnerResult = await getPartnerIds(tgId);
            setHasPartnerId(!!partnerResult?.has_partner_id);
          } catch (err) {
            console.error("Ошибка проверки partner_id:", err);
            setHasPartnerId(false);
          }
        }

        setLoading(false);
      };

      checkStatus();
    } else {
      setIsAuthorized(false);
      setLoading(false);
    }
  }, []);

  const openSupportChat = () => {
    const supportUrl = 'https://t.me/Suprun_support';
    if (window.Telegram && window.Telegram.WebApp) {
      window.Telegram.WebApp.openTelegramLink(supportUrl);
    } else {
      window.open(supportUrl, '_blank');
    }
  };

  const goToBotForAuth = () => {
    const botUsername = 'SuprunKEY_bot';
    const deepLink = `https://t.me/${botUsername}?start=auth_${telegramId}`;

    if (window.Telegram && window.Telegram.WebApp) {
      window.Telegram.WebApp.openTelegramLink(deepLink);
    } else {
      window.open(deepLink, '_blank');
    }
  };

  const handleSelect = (vpn) => {
    setSelectedVPN(vpn);
    navigate('/subscription');
  };

  const icons = {
    outline: Shield,
    v2ray: Zap,
    v2raybypass: Globe,
  };

  if (loading) {
    return (
      <div className="main-wrapper">
        <div className="content" style={{ textAlign: 'center', paddingTop: '100px' }}>
          <p>Проверка статуса...</p>
        </div>
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div className="main-wrapper">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="content unauthorized-screen"
        >
          <div className="header">
            <img src={Logo} alt="SuprunVPN" className="logo-image" />
          </div>

          <div className="unauthorized-card">
            <AlertCircle size={64} strokeWidth={2} className="warning-icon" />
            <h2>Вы не авторизованы</h2>
            <p>
              Ваш Telegram ID не найден в системе.<br />
              Пожалуйста, авторизуйтесь через бота.
            </p>

            <motion.button
              className="auth-btn"
              whileTap={{ scale: 0.95 }}
              onClick={goToBotForAuth}
            >
              Авторизоваться
            </motion.button>

            <motion.button
              className="support-btn"
              style={{ marginTop: '20px' }}
              whileTap={{ scale: 0.95 }}
              onClick={openSupportChat}
            >
              <MessageCircle size={20} strokeWidth={2} className="support-icon" />
              Тех Поддержка
            </motion.button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Авторизованный пользователь → основной экран
  return (
    <div className="main-wrapper">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="content"
      >
        <div className="header">
          <img src={Logo} alt="SuprunVPN" className="logo-image" />
        </div>

        <motion.button
          className="subscriptions-btn"
          whileTap={{ scale: 0.95 }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          onClick={() => navigate('/management')}
        >
          <Key size={24} strokeWidth={2} className="subscriptions-icon" />
          Мои подписки
        </motion.button>

        {showRaffleButton && (
          <motion.button
            className="raffle-btn"
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            onClick={() => navigate('/gift')}
          >
            <Gift size={24} strokeWidth={2} className="raffle-icon" />
            Розыгрыш призов
          </motion.button>
        )}

        <div className="cards-grid">
          {vpnTypes.map((vpn, index) => {
            const Icon = icons[vpn.id];
            return (
              <motion.button
                key={vpn.id}
                className={`vpn-card ${vpn.id === 'v2raybypass' ? 'full-width-card' : ''}`}
                onClick={() => handleSelect(vpn)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 + 0.2 }}
                whileTap={{ scale: 0.95 }}
              >
                <div className="card-icon">
                  <Icon size={28} strokeWidth={2} />
                </div>
                <h3 className="card-name">{vpn.name}</h3>
                <p className="card-desc">{vpn.description}</p>
              </motion.button>
            );
          })}
        </div>

        {hasPartnerId && (
          <motion.button
            className="partner-btn"
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75 }}
            onClick={() => navigate('/partnership')}
          >
            <Users size={20} strokeWidth={2} className="partner-icon" />
            Партнёрство
          </motion.button>
        )}

        <motion.button
          className="support-btn"
          whileTap={{ scale: 0.95 }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: hasPartnerId ? 0.8 : 0.75 }}
          onClick={openSupportChat}
        >
          <MessageCircle size={20} strokeWidth={2} className="support-icon" />
          Тех Поддержка
        </motion.button>
        {/* <div className="legal-links">
          <a
            href="https://telegra.ph/Politika-konfidencialnosti-08-21-102"
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.preventDefault();
              if (window.Telegram?.WebApp) {
                window.Telegram.WebApp.openLink(
                  'https://telegra.ph/Politika-konfidencialnosti-08-21-102'
                );
              } else {
                window.open(
                  'https://telegra.ph/Politika-konfidencialnosti-08-21-102',
                  '_blank'
                );
              }
            }}
          >
            Политика конфиденциальности
          </a>
          <span className="separator">·</span>
          <a
            href="https://telegra.ph/Polzovatelskoe-soglashenie-08-21-71"
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.preventDefault();
              if (window.Telegram?.WebApp) {
                window.Telegram.WebApp.openLink(
                  'https://telegra.ph/Polzovatelskoe-soglashenie-08-21-71'
                );
              } else {
                window.open(
                  'https://telegra.ph/Polzovatelskoe-soglashenie-08-21-71',
                  '_blank'
                );
              }
            }}
          >
            Пользовательское соглашение
          </a>
        </div> */}
      </motion.div>
    </div>
  );
}

export default MainScreen;